import React, { useState } from 'react';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { MapPin, Phone, Mail, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { sendEmail } from '../utils/emailService';
import { useLanguage } from '../utils/i18n';
import { getSiteConfig } from '../utils/siteConfig';
interface FormState {
  name: string;
  phone: string;
  email: string;
  message: string;
}
interface FormErrors {
  name?: string;
  phone?: string;
}
export function ContactSection() {
  const {
    t
  } = useLanguage();
  const config = getSiteConfig();
  const contacts = config.contacts;
  const [form, setForm] = useState<FormState>({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.name.trim()) newErrors.name = t('contact_form_err_name');
    if (!form.phone.trim()) {
      newErrors.phone = t('contact_form_err_phone');
    } else if (!/^[\d\s\+\-\(\)]{7,}$/.test(form.phone.trim())) {
      newErrors.phone = t('contact_form_err_phone_invalid');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('loading');
    const result = await sendEmail({
      from_name: form.name,
      phone: form.phone,
      email: form.email,
      message: form.message,
      form_type: 'Контактная форма'
    });
    setStatus(result.ok ? 'success' : 'error');
  };
  return <Section id="contact" className="bg-gray-50" data-id="element-72">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-73">
        <div data-id="element-74">
          <h2 className="text-gray-500 font-bold tracking-widest uppercase mb-3 text-sm" data-id="element-75">
            {t('contact_label')}
          </h2>
          <h3 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 uppercase mb-8 leading-tight" data-id="element-76">
            {t('contact_title_1')}{' '}
            <span className="text-red-600" data-id="element-77">{t('contact_title_2')}</span>
          </h3>

          <div className="space-y-8 mb-12" data-id="element-78">
            <div className="flex items-start gap-4" data-id="element-79">
              <div className="bg-white p-3 shadow-sm rounded text-gray-700" data-id="element-80">
                <MapPin className="h-6 w-6" data-id="element-81" />
              </div>
              <div data-id="element-82">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-83">
                  {t('contact_office')}
                </h4>
                <p className="text-gray-600" data-id="element-84">{contacts.address}</p>
              </div>
            </div>

            <div className="flex items-start gap-4" data-id="element-85">
              <div className="bg-white p-3 shadow-sm rounded text-gray-700" data-id="element-86">
                <Phone className="h-6 w-6" data-id="element-87" />
              </div>
              <div data-id="element-88">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-89">
                  {t('contact_phone')}
                </h4>
                <a href={`tel:${contacts.phone.replace(/\s/g, '')}`} className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-90">
                  {contacts.phone}
                </a>
                <p className="text-gray-500 text-sm" data-id="element-91">
                  {t('contact_multichannel')}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4" data-id="element-92">
              <div className="bg-white p-3 shadow-sm rounded text-gray-700" data-id="element-93">
                <Mail className="h-6 w-6" data-id="element-94" />
              </div>
              <div data-id="element-95">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-96">
                  {t('contact_email')}
                </h4>
                <a href={`mailto:${contacts.email || 'info@yavuzburak.kg'}`} className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-97">
                  {contacts.email || 'info@yavuzburak.kg'}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4" data-id="element-98">
              <div className="bg-white p-3 shadow-sm rounded text-gray-700" data-id="element-99">
                <Mail className="h-6 w-6" data-id="element-100" />
              </div>
              <div data-id="element-101">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-102">Instagram</h4>
                <a href={config.social.instagram || 'https://instagram.com/yavuzburak.kg'} target="_blank" rel="noopener noreferrer" className="text-gray-900 hover:underline font-medium" data-id="element-103">
                  {contacts.instagram || '@yavuzburak.kg'}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4" data-id="element-104">
              <div className="bg-white p-3 shadow-sm rounded text-gray-700" data-id="element-105">
                <Clock className="h-6 w-6" data-id="element-106" />
              </div>
              <div data-id="element-107">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-108">
                  {t('contact_workhours')}
                </h4>
                <p className="text-gray-600" data-id="element-109">{contacts.workdaysHours}</p>
                <p className="text-gray-600" data-id="element-110">{contacts.weekendHours}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 md:p-10 rounded-sm shadow-lg border-t-4 border-gray-900" data-id="element-111">
          <h4 className="font-heading text-2xl font-bold text-gray-900 mb-6" data-id="element-112">
            {t('contact_form_title')}
          </h4>

          {status === 'success' ? <div className="text-center py-12" data-id="element-113">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-114">
                <CheckCircle className="h-8 w-8 text-green-600" data-id="element-115" />
              </div>
              <h5 className="font-heading text-xl font-bold text-gray-900 mb-2" data-id="element-116">
                {t('contact_form_success')}
              </h5>
              <p className="text-gray-600 text-sm mb-6" data-id="element-117">
                {t('contact_form_success_desc')}
              </p>
              <button onClick={() => {
            setStatus('idle');
            setForm({
              name: '',
              phone: '',
              email: '',
              message: ''
            });
          }} className="text-red-600 hover:underline text-sm font-medium" data-id="element-118">
                {t('contact_form_btn_another')}
              </button>
            </div> : <form onSubmit={handleSubmit} className="space-y-6" noValidate data-id="element-119">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-120">
                <div className="space-y-1" data-id="element-121">
                  <label className="text-sm font-medium text-gray-700" data-id="element-122">
                    {t('contact_form_name')}{' '}
                    <span className="text-red-500" data-id="element-123">*</span>
                  </label>
                  <input name="name" type="text" value={form.name} onChange={handleChange} className={`w-full px-4 py-3 border rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all ${errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200'}`} placeholder={t('contact_name_placeholder')} data-id="element-124" />

                  {errors.name && <p className="text-xs text-red-600 flex items-center gap-1" data-id="element-125">
                      <AlertCircle className="h-3 w-3" data-id="element-126" />
                      {errors.name}
                    </p>}
                </div>
                <div className="space-y-1" data-id="element-127">
                  <label className="text-sm font-medium text-gray-700" data-id="element-128">
                    {t('contact_form_phone')}{' '}
                    <span className="text-red-500" data-id="element-129">*</span>
                  </label>
                  <input name="phone" type="tel" value={form.phone} onChange={handleChange} className={`w-full px-4 py-3 border rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all ${errors.phone ? 'border-red-400 bg-red-50' : 'border-gray-200'}`} placeholder="+996 (___) ___-__-__" data-id="element-130" />

                  {errors.phone && <p className="text-xs text-red-600 flex items-center gap-1" data-id="element-131">
                      <AlertCircle className="h-3 w-3" data-id="element-132" />
                      {errors.phone}
                    </p>}
                </div>
              </div>

              <div className="space-y-1" data-id="element-133">
                <label className="text-sm font-medium text-gray-700" data-id="element-134">
                  {t('contact_email')}
                </label>
                <input name="email" type="email" value={form.email} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all" placeholder="example@mail.com" data-id="element-135" />
              </div>

              <div className="space-y-1" data-id="element-136">
                <label className="text-sm font-medium text-gray-700" data-id="element-137">
                  {t('contact_form_msg')}
                </label>
                <textarea name="message" rows={4} value={form.message} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all resize-none" placeholder={t('contact_msg_placeholder')} data-id="element-138" />
              </div>

              <Button className="w-full" size="lg" isLoading={status === 'loading'} data-id="element-139">
                {t('contact_form_submit')}
              </Button>

              <p className="text-xs text-center text-gray-500" data-id="element-140">
                {t('contact_form_policy')}
              </p>
            </form>}
        </div>
      </div>
    </Section>;
}